<script setup lang="ts">

import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import MediumPlusModel from '@/components/Interface/Modal/ModelSizes/MediumPlusModel.vue'
import ConfigPQAAvailable from '@/components/Interface/Modal/Modals/ModalConfigPQA/ConfigPQAAvailable.vue'
import { computed, onMounted, ref } from 'vue'
import { useAssetStore } from '@/stores/assetStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { usePQAStore } from '@/stores/PQAStore.ts'
import ConfigPQASelected from '@/components/Interface/Modal/Modals/ModalConfigPQA/ConfigPQASelected.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'

/**
 * Props
 */

/**
 * Stores / models
 */
const gameStore = useGameStore()
const PQAStore = usePQAStore()
const assetStore = useAssetStore()

/**
 * Variables
 */
const type = ref('')
const list = [
  'food','production','science','credit','energy','energy_storage',
  'ore_storage','scanning_range','defence_attack_ships','defence_shields',
  'ore_refinery','ore'
];

/**
 * Computed
 */
const availableBuildings = computed(() => {
  let filtered: Map<any, any>;

  if (type.value === '') {
    filtered = assetStore.findAllBuildingProductionObjects();
  } else {
    filtered = assetStore.findBuildingProductionObjectsByEffectType(type.value);
  }

  // Sort by production cost
  return new Map([...filtered.entries()].sort((a, b) => a[1].productionCost - b[1].productionCost));
});


/**
 * Methods
 */
const clearQueue = () => {
  const count = PQAStore.selectedQueueData.length
  PQAStore.clearQueue()
  gameStore.setTinySuccessToastMessage(`Queue cleared! ${count} buildings removed`)
}

const exportQueue = () => {
  try {
    // Get building codes from store
    const buildingCodes = PQAStore.exportQueueToBuildingCodes()

    // Create the data object to export
    const exportData = {
      version: '1.0',
      buildingCodes: buildingCodes,
      exportedAt: new Date().toISOString()
    }

    // Convert to JSON
    const jsonString = JSON.stringify(exportData, null, 2)

    // Create a blob and download
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'buildingqueue.ferion'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    // Success toast
    gameStore.setTinySuccessToastMessage(`Export successful! ${buildingCodes.length} buildings exported`)
  } catch (error) {
    gameStore.setTinyErrorToastMessage('Export failed')
  }
}

const importQueue = () => {
  // Create a hidden file input
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.ferion'

  input.onchange = (e: Event) => {
    const target = e.target as HTMLInputElement
    const file = target.files?.[0]

    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string
        const importData = JSON.parse(content)

        // Validate the data structure
        if (importData.buildingCodes && Array.isArray(importData.buildingCodes)) {
          // Import using store method
          const result = PQAStore.importQueueFromBuildingCodes(importData.buildingCodes)

          // Show success/warning toast
          if (result.missing > 0) {
            gameStore.setTinyErrorToastMessage(`Import incomplete: ${result.missing} buildings not found`)
          } else {
            gameStore.setTinySuccessToastMessage(`Import successful! ${result.success} buildings imported`)
          }
        } else {
          gameStore.setTinyErrorToastMessage('Invalid file format: missing buildingCodes array')
        }
      } catch (error) {
        gameStore.setTinyErrorToastMessage('Failed to import queue')
      }
    }

    reader.readAsText(file)
  }

  input.click()
}

/**
 * Lifecycle
 */
onMounted(() => {
  PQAStore.selectQueue(gameStore.modalData)
})
</script>

<template>
  <MediumPlusModel title="Planet Production Automation">
    <div class="text-sm font-medium text-center border-b text-slate-400 border-slate-700 sm:hidden">
      <div class="overflow-x-auto whitespace-nowrap no-scrollbar">
        <ul class="flex">
          <li class="me-2">
            <a href="#"
               :class="{
                 'active': PQAStore.selectedModalMobileTab === 'queue',
                 'text-blue-500 border-blue-500': PQAStore.selectedModalMobileTab === 'queue',
                 'text-slate-500 border-transparent': PQAStore.selectedModalMobileTab !== 'queue',
                 'inline-block p-4 border-b-2 rounded-t-lg  hover:border-slate-300 hover:text-slate-300': true
             }"
               @click="PQAStore.selectedModalMobileTab = 'queue'">
              Queue
            </a>
          </li>
          <li class="me-2">
            <a href="#"
               :class="{
                 'active': PQAStore.selectedModalMobileTab === 'available',
                 'text-blue-500 border-blue-500': PQAStore.selectedModalMobileTab === 'available',
                 'text-slate-500 border-transparent': PQAStore.selectedModalMobileTab !== 'available',
                 'inline-block p-4 border-b-2 rounded-t-lg hover:border-slate-300 hover:text-slate-300': true
             }"
               @click="PQAStore.selectedModalMobileTab = 'available'">
              Available
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div class="flex justify-between p-2">
      <div :class="{
        'pr-0 md:pr-2 w-full md:block': true,
        'hidden' : PQAStore.selectedModalMobileTab === 'available'
      }">
        <StandardCard title="Queue" help="Changes are saved automatically" top-padding="0" bottom-padding="0"></StandardCard>
        <div class="flex">
          <div class="pt-1">
            <div
              @click="clearQueue()"
              :class="{
                ' p-2 mb-1 me-1 w-10 flex items-center justify-center cursor-pointer text-slate-300 rounded bg-slate-800':true
              }">
              <GenericIcon type="trash" tooltip="Clear"/>
            </div>
            <div
              @click="exportQueue()"
              :class="{
                ' p-2 mb-1 me-1 w-10 flex items-center justify-center cursor-pointer text-slate-300 rounded bg-slate-800':true
              }">
              <GenericIcon type="export" tooltip="Export"/>
            </div>
            <div
              @click="importQueue()"
              :class="{
                ' p-2 mb-1 me-1 w-10 flex items-center justify-center cursor-pointer text-slate-300 rounded bg-slate-800':true
              }">
              <GenericIcon type="import" tooltip="Import"/>
            </div>
          </div>
          <div class="scroll-height w-full">
            <template
              v-for="(productionObjectID,index) in PQAStore.selectedQueueData" :key="index"
            >
              <ConfigPQASelected
                :productionObjectId="productionObjectID"
                :PQAId="gameStore.modalData"
                :index="index"/>
            </template>
          </div>
        </div>
      </div>
      <div :class="{
        'w-full md:block': true,
        'hidden' : PQAStore.selectedModalMobileTab === 'queue'
      }">
        <StandardCard title="Available" help="Select the buildings you want to add to your Building Queue." top-padding="0" bottom-padding="0"></StandardCard>
        <div class="flex">
          <div class="pt-1">
            <div
              @click="type = ''"
              :class="{
                  ' p-2 mb-1 px-3 me-1 flex items-center justify-center cursor-pointer text-slate-300 rounded':true,
                  'bg-slate-700': type === '',
                  'bg-slate-800': type !== ''
                }">
              All
            </div>
            <template v-for="item in list" :key="item">
              <div
                @click="type = item"
                :class="{
                  ' p-2 mb-1 me-1 flex items-center justify-center cursor-pointer rounded':true,
                  'bg-slate-700': type === item,
                  'bg-slate-800': type !== item
                }">
                <GenericIcon :type="item" :class="{'slatescale' : type !== item}"/>
              </div>
            </template>
          </div>
        <div class="scroll-height w-full">
          <template
            v-for="productionObject in Array.from(availableBuildings.values())"
            :key="productionObject.id"
          >
            <ConfigPQAAvailable
              :productionObjectId="productionObject.id"
              :PQAId="gameStore.modalData"
            />
          </template>
        </div>
        </div>
      </div>
    </div>
  </MediumPlusModel>
</template>

<style scoped lang="scss">
.scroll-height {
  height: calc(100vh - 13rem);
  overflow-y: auto;
}
</style>